import type { TranslationKeys } from '@/i18n/types';

export const ptBR: TranslationKeys = {
  common: {
    loading: 'Carregando...',
    error: 'Algo deu errado',
    retry: 'Tentar novamente',
    save: 'Salvar',
    cancel: 'Cancelar',
    continue: 'Continuar',
    skip: 'Pular',
    back: 'Voltar',
    next: 'Próximo',
    close: 'Fechar',
    search: 'Buscar',
    settings: 'Configurações',
  },
  greeting: {
    morning: 'Bom dia',
    afternoon: 'Boa tarde',
    evening: 'Boa noite',
  },
  auth: {
    signIn: 'Entrar',
    signOut: 'Sair',
    signUp: 'Criar conta',
    continueWith: 'Continuar com {{provider}}',
    orContinueWith: 'ou continue com',
    magicLink: 'Entrar com email',
    magicLinkSent: 'Link enviado! Verifique seu email.',
    sentTo: 'Enviamos um link para {{email}}',
    emailPlaceholder: 'seu@email.com',
    errors: {
      invalidEmail: 'Email inválido',
      generic: 'Erro ao autenticar. Tente novamente.',
      rateLimited: 'Muitas tentativas. Aguarde um minuto.',
      invalidCredentials: 'Email ou senha inválidos.',
    },
  },
  onboarding: {
    welcome: 'Bem-vindo à Newsletter',
    step1Title: 'Qual é o seu perfil?',
    step1Subtitle: 'Selecione um ou mais perfis que melhor descrevem você.',
    step2Title: 'Conte mais sobre você',
    step2Subtitle: 'Essas informações são opcionais e ajudam a personalizar seu feed.',
    profiles: {
      engineer: 'Engenheiro',
      engineer_desc: 'Desenvolvimento, arquitetura, DevOps',
      product_manager: 'Product Manager',
      product_manager_desc: 'Gestão de produto, estratégia',
      designer: 'Designer',
      designer_desc: 'UX, UI, design de produto',
      founder: 'Founder / Líder',
      founder_desc: 'Empreendedor, C-level, liderança',
      generalist: 'Generalista',
      generalist_desc: 'Interesse amplo em tecnologia',
    },
    role: 'Cargo',
    rolePlaceholder: 'Selecione seu cargo',
    industry: 'Indústria',
    industryPlaceholder: 'Selecione sua indústria',
    letsGo: 'Vamos lá!',
  },
  feed: {
    title: 'Feed',
    empty: 'Nada novo por aqui',
    emptySubtitle: 'Volte mais tarde para novas leituras.',
    endOfFeed: 'Isso é tudo por hoje',
    endOfFeedSubtitle: 'Você está em dia com as novidades.',
    slowDay: 'Dia calmo hoje',
    slowDaySubtitle: 'Que tal revisitar alguns clássicos?',
    pullToRefresh: 'Puxe para atualizar',
    refreshing: 'Atualizando...',
  },
  content: {
    readOriginal: 'Ver original',
    readInPortuguese: 'Ver em português',
    share: 'Compartilhar',
    shareOn: 'Compartilhar no',
    copyLink: 'Copiar link',
    linkCopied: 'Link copiado!',
    readingTime: '{{minutes}} min de leitura',
    previous: 'Anterior',
    next: 'Próximo',
  },
  actions: {
    like: 'Útil',
    liked: 'Marcado como útil',
    save: 'Salvar',
    saved: 'Salvo',
    dismiss: 'Pular',
    dismissed: 'Pulado',
    more: 'Mais',
    lessLikeThis: 'Menos assim',
    muteAuthor: 'Silenciar autor',
    muteSource: 'Silenciar fonte',
    undoSave: 'Desfazer',
    removed: 'Removido',
  },
  saved: {
    title: 'Salvos',
    empty: 'Nada salvo ainda',
    emptySubtitle: 'Salve artigos para ler depois.',
    savedAgo: 'Salvo há {{time}}',
    remove: 'Remover',
  },
  time: {
    now: 'Agora',
    hoursAgo: '{{count}}h',
    daysAgo: '{{count}}d',
    weeksAgo: '{{count}} sem',
    monthsAgo: '{{count}} mês',
  },
  errors: {
    network: 'Erro de conexão. Verifique sua internet.',
    notFound: 'Conteúdo não encontrado',
    unauthorized: 'Você precisa estar logado',
    generic: 'Algo deu errado. Tente novamente.',
  },
  landing: {
    heroTitle: 'O que você\nprecisa ler sobre\ntecnologia.',
    heroSubtitle: 'O essencial, todo dia. Curadoria feita com tecnologia.',
    heroButton: 'Ler agora',
    ctaSecondary: 'Como funciona',
    whyTitle: 'Por que Newsletter da Vector?',
    whySubtitle:
      'Três razões que fazem da Newsletter a melhor forma de acompanhar o mundo da tecnologia.',
    feature1Title: 'Buscamos',
    feature1Desc:
      'Selecionamos manualmente os melhores conteúdos sobre tecnologia. Cada artigo é escolhido por especialistas que entendem o que realmente importa para você.',
    feature2Title: 'Filtramos',
    feature2Desc:
      'Nossa plataforma aprende com suas preferências. Quanto mais você lê, mais personalizada se torna sua experiência de descoberta de conteúdo.',
    feature3Title: 'O essencial',
    feature3Desc:
      'Leve suas leituras para qualquer lugar. Salve artigos e acesse-os mesmo sem conexão, transformando qualquer momento em uma oportunidade de aprendizado.',
    ctaTitle: 'Pronto para começar?',
    ctaButton: 'Criar conta gratuita',
    footerRights: 'Todos os direitos reservados.',
  },
  consent: {
    title: 'Sua privacidade',
    description:
      'Usamos cookies de análise para entender como você interage com a Newsletter e melhorar sua experiência. Seus dados são tratados conforme nossa',
    accept: 'Aceitar',
    decline: 'Recusar',
    privacyLink: 'Política de Privacidade',
  },
  pwa: {
    title: 'Instalar Newsletter',
    description: 'Adicione à tela inicial para acesso rápido e experiência offline.',
    install: 'Instalar',
    dismiss: 'Agora não',
  },
  themes: {
    ai_ml: 'AI / Machine Learning',
    engineering: 'Engenharia',
    product: 'Produto',
    design: 'Design',
    startup: 'Startups',
    leadership: 'Liderança',
    career: 'Carreira',
  },
  newsletter: {
    title: 'Receba o melhor da tecnologia',
    subtitle: 'Curadoria diária direto no seu email. Sem spam, só o essencial.',
    placeholder: 'seu@email.com',
    subscribe: 'Inscrever-se',
    subscribing: 'Inscrevendo...',
    success: 'Inscrição confirmada! Fique de olho no seu email.',
    alreadySubscribed: 'Este email já está inscrito.',
    error: 'Erro ao inscrever. Tente novamente.',
    invalidEmail: 'Email inválido',
  },
  admin: {
    title: 'Revisão de Conteúdo',
    backToFeed: 'Feed',
    filters: {
      status: {
        pending: 'Pendente',
        reviewed: 'Revisado',
        flagged: 'Sinalizados',
        newsletter: 'Newsletter',
        all: 'Todos',
      },
      period: 'Período',
      periodOptions: {
        all: 'Todos',
        day: '24h',
        week: '7 dias',
        month: '30 dias',
      },
      confidence: 'Confiança',
    },
    summary: {
      total: 'Total',
      items: 'itens',
      reviewed: 'revisados',
      pending: 'pendentes',
      flagged: 'sinalizados',
    },
    list: {
      searchPlaceholder: 'Buscar conteúdo...',
      noContent: 'Nenhum conteúdo encontrado',
      revisions: 'rev',
      noDate: 'Sem data',
      showingAll: 'Mostrando todos os {{count}} itens',
    },
    detail: {
      selectContent: 'Selecione um conteúdo para revisar',
      contentNotFound: 'Conteúdo não encontrado',
      language: 'Idioma',
      revisionsCount: 'Revisões',
      humanReviewed: 'Revisado por humano',
      edit: 'Editar',
      resetToAI: 'Restaurar IA',
    },
    fields: {
      title: 'Título',
      summary: 'Resumo',
      category: 'Categoria',
      author: 'Autor',
      tags: 'Tags',
      theme: 'Tema',
      themeSuggested: 'Tema (sugerido)',
      nature: 'Natureza',
      depth: 'Profundidade',
      timeliness: 'Atualidade',
      originality: 'Originalidade',
      authorityScore: 'Autoridade',
      densityScore: 'Densidade',
      primarySubject: 'Assunto',
      mentions: 'Menções',
      modified: 'Modificado',
      generated: 'Gerado',
      current: 'Atual',
      notGenerated: 'Não gerado',
      noTagsGenerated: 'Nenhuma tag gerada',
      noTagsSelected: 'Nenhuma tag selecionada',
      suggested: 'sugerido',
      notClassified: 'Não classificado',
    },
    classification: {
      title: 'Classificação',
      theme: 'Tema',
      nature: 'Natureza',
      depth: 'Profundidade',
      timeliness: 'Atualidade',
      originality: 'Originalidade',
    },
    quality: {
      title: 'Indicadores de Qualidade',
      authorityScore: 'Autoridade',
      authorityDesc: 'Credibilidade do autor/fonte',
      densityScore: 'Densidade',
      densityDesc: 'Densidade de informação',
    },
    subjects: {
      title: 'Assuntos',
      primary: 'Assunto Principal',
      mentions: 'Menções',
      notIdentified: 'Não identificado',
      noneIdentified: 'Nenhuma identificada',
    },
    revisionHistory: {
      title: 'Histórico de Revisões',
      noFields: 'Sem campos',
    },
    fullContent: {
      title: 'Conteúdo Completo',
      noContent: 'Nenhum conteúdo disponível',
    },
    content: {
      title: 'Conteúdo do Artigo',
      rendered: 'Renderizado',
      html: 'HTML',
    },
    comments: {
      title: 'Comentários',
      placeholder: 'Adicionar um comentário...',
      noComments: 'Nenhum comentário ainda',
    },
    status: {
      approved: 'Aprovado',
      pending: 'Pendente',
      rejected: 'Rejeitado',
      flagged: 'Sinalizado',
      notReviewed: 'Não revisado',
      reviewedAt: 'Revisado em: {{date}}',
    },
    actions: {
      saveChanges: 'Salvar Alterações',
      approve: 'Aprovar',
      reject: 'Rejeitar',
      flag: 'Sinalizar',
      unflag: 'Remover Sinalização',
      flagTooltip: 'Sinalizar conteúdo (ocultar do feed)',
      unflagTooltip: 'Remover sinalização do conteúdo',
      translate: 'Traduzir para pt-BR',
      translateTooltip: 'Gerar versão em português deste conteúdo',
    },
    newsletter: {
      viewLink: 'Ver artigos',
      sentAt: 'Enviado',
      recipients: 'destinatários',
      sourceArticles: 'Artigos Fonte',
    },
  },
};
